import { ErrorLocale } from "@/locales/localeid";
import { RoomSessionModel } from "@/models";
import { GLErrorCode } from "@/models/error.model";
import router from "@/router";
import { InfoService, OneToOneDto, RemoteTeachingService, ToggleStudentMediaDevicesAsync } from "@/services";
import { IndependentService } from "@/services/independent";
import { UserRole } from "@/store/app/state";
import { StudyMode } from "@/store/teaching/interfaces";
import { AGORA_MINIMUM_USER_VOLUME } from "@/utils/constant";
import { Logger } from "@/utils/logger";
import { Paths } from "@/utils/paths";
import { isTeacherUseOnlySlideVisible, MediaDeviceTypes, showUnableJoinClassErrorNotification } from "@/utils/utils";
import { REALTIME_EVENT, StudentAction, StudentUpdateIndependentExposureEventData, UpdateIndependentExposureItemModel } from "@/ws";
import { ConnectionDisconnectedReason, ConnectionState, IAgoraRTCRemoteUser, UID } from "agora-rtc-sdk-ng";
import { ErrorCode, fmtMsg } from "vue-glcommonui";
import { ActionTree } from "vuex";
import { VideoCallStatus, NetworkQualityPayload } from "../interface";
import { useStudentRealtimeHandler } from "./handler";
import { ClassActionFromValue, StudentRoomState } from "./state";
import { callingUtils } from "@/store/room/student/utils";
import { AgoraEventHandler } from "@/agora";
import { TeachingActivityTarget } from "@/store/lesson/state";
import { notification } from "ant-design-vue";
import { defineVuexName, vuexName, MODULES } from "@/store/utils";
import { MediaUserPublishStatus } from "@/store/calling/utils";
import { CallingUserStatus } from "@/store/calling/mutations";
import { applySettings } from "@/store/realtime-settings/utils";
import { mapObjectsFromBE } from "@/store/annotation-v2/actions";
import { debounce } from "lodash";

export interface SetStudentMediaDeviceAction {
  enable: boolean;
}

const debouncedUpdateIndependentItem = debounce(async (request: UpdateIndependentExposureItemModel) => {
  await RemoteTeachingService.updateIndependentExposureItem(request);
}, 500);

const actions: ActionTree<StudentRoomState, any> = {
  async [defineVuexName(MODULES.STUDENT_ROOM.DISPATCHES.GET_CLASS_ROOM_INFO)]({ dispatch, state }) {
    if (!state.info?.id) return;
    const { data: roomInfo } = await RemoteTeachingService.fetchSessionDtoById(state.info.id);
    await dispatch(vuexName(MODULES.STUDENT_ROOM.DISPATCHES.SET_CLASS_DATA, false), roomInfo);
  },
  async [defineVuexName(MODULES.STUDENT_ROOM.DISPATCHES.INIT_CLASS_ROOM)](
    { commit, dispatch },
    payload: {
      classId: string;
      userId: string;
      userName: string;
      studentId: string;
      role: string;
      deviceId: string;
      callFirstTime: boolean;
    },
  ) {
    commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_USER, false), {
      id: payload.studentId,
      name: payload.userName,
    });
    try {
      const setIndependentModeData = async () => {
        try {
          const response = await IndependentService.getIndependentStudentItem(payload.studentId);
          commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_MODE), StudyMode.Independent, { root: true });
          commit(vuexName(MODULES.STUDENT_TEACHING.COMMITS.SET_CURRENT_INDEPENDENT_ITEM_ID), response.data, { root: true });
        } catch (err) {
          Logger.error(err);
        }
      };
      const setOneToOneWithHelperData = async (oneToOneWithHelperDto: OneToOneDto) => {
        const { isIgnoreTeacherAudio } = oneToOneWithHelperDto;
        commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_ONE_TO_ONE_WITH_HELP_IGNORE_TEACHER_VOICE), isIgnoreTeacherAudio, { root: true });
      };
      const setClassTeachingData = (sessionInfo: { sessionId: string; groupId: string; classId: string }) => {
        commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_SESSION_INFO), sessionInfo, { root: true });
      };
      //** This is the only time to check if the teacher has lost connection using our server, then it will rely on AGORA */
      const checkTeacherDisconnect = (teacherCallingConnectionStatus: VideoCallStatus) => {
        commit(
          vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_TEACHER_DISCONNECTED, false),
          teacherCallingConnectionStatus === VideoCallStatus.Disconnected,
        );
      };
      const setTeamModeData = async (isTeamMode: boolean) => {
        commit(vuexName(MODULES.TEAMS.COMMITS.SET_IS_TEAM_MODE), isTeamMode, { root: true });
        await dispatch(vuexName(MODULES.TEAMS.DISPATCHES.GET_TEAMS), null, { root: true });
      };
      const { data: roomInfo } = await RemoteTeachingService.studentGetRoomInfo(payload.studentId, payload.deviceId);
      const invalidClassInfo = roomInfo?.classInfo?.classId !== payload.classId;
      commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_API_STATUS, false), {
        code: invalidClassInfo ? GLErrorCode.CLASS_IS_NOT_ACTIVE : GLErrorCode.SUCCESS,
        message: invalidClassInfo ? fmtMsg(ErrorLocale.ClassNotStarted) : "",
      });
      if (invalidClassInfo) return;
      if (payload.callFirstTime) {
        checkTeacherDisconnect(roomInfo.teacher.videoCallStatus);
      }
      setClassTeachingData({ sessionId: roomInfo.id, classId: roomInfo.classInfo.classId, groupId: roomInfo.classInfo.groupId });
      commit(vuexName(MODULES.WHITE_BOARD.COMMITS.SET_SHOULD_FETCH_MEDIA_STATE), true, { root: true });
      if (roomInfo.state.independentMode) {
        await setIndependentModeData();
      }
      await setTeamModeData(roomInfo.state.isTeamMode);
      if (payload.studentId === roomInfo.helperOneAndOneDto?.studentId) {
        await setOneToOneWithHelperData(roomInfo.helperOneAndOneDto);
      }
      // TODO: Refactor lesson data handling logic
      if (!payload.callFirstTime && !roomInfo.lessonPlan.selectedExposureId) {
        // Clear exposure and exposure item if not set on Redis after successful reconnection
        commit(vuexName(MODULES.LESSON.COMMITS.CLEAR_EXPOSURE), null, { root: true });
      }
      await dispatch(vuexName(MODULES.STUDENT_ROOM.DISPATCHES.SET_CLASS_DATA, false), roomInfo);
    } catch (error) {
      Logger.error(error);
      await dispatch(vuexName(MODULES.STUDENT_ROOM.DISPATCHES.SET_API_ERROR, false), error);
    }
  },
  async [defineVuexName(MODULES.STUDENT_ROOM.DISPATCHES.MANAGE_REMOTE_USER_VIDEO_AND_AUDIO_SUBSCRIPTIONS)]({ state, rootGetters }) {
    const removeUserFromLists = (userId: string, videoList: string[], audioList: string[]) => {
      const videoIndex = videoList.indexOf(userId);
      if (videoIndex > -1) videoList.splice(videoIndex, 1);
      const audioIndex = audioList.indexOf(userId);
      if (audioIndex > -1) audioList.splice(audioIndex, 1);
    };
    const {
      manager,
      teacher,
      idOne: oneToOneStudentId,
      idHelperOne: helperOneToOneStudentId,
      student,
      videosFeedVisible: isShowClassmateVideos,
      helper,
    } = state;
    if (!teacher || !student || !manager?.agoraClient?.joined) return;

    const meId = student.id;
    const isMeOneToOneWithHelper = helperOneToOneStudentId === meId;
    const isMeOneToOneWithTeacher = oneToOneStudentId === meId;

    let remoteVideoUserIds: string[] = rootGetters[vuexName(MODULES.CALLING.GETTERS.GET_VIDEO_USER_IDS)].filter((id: string) => id !== meId);
    let remoteAudioUserIds: string[] = rootGetters[vuexName(MODULES.CALLING.GETTERS.GET_AUDIO_USER_IDS)].filter((id: string) => id !== meId);

    const isHelperJoinClass = !!helper;

    // If independent mode is enabled or classmate videos are hidden, only subscribe to the teacher/helper's video and audio
    const isIndependentModeEnabled = rootGetters[vuexName(MODULES.CLASS_TEACHING.GETTERS.GET_IS_INDEPENDENT_MODE_ENABLED)];

    if (rootGetters["isHideClassmateVideosOnLowBandwidthEnabled"]) {
      const filterIds = (ids: string[]) => ids.filter((id) => id === teacher.id || (isHelperJoinClass && id === helper.id));
      // In low bandwidth condition, we only subscribe to the teacher's and helper's video
      remoteVideoUserIds = filterIds(remoteVideoUserIds);
    }

    if (isIndependentModeEnabled || !isShowClassmateVideos) {
      const filterIds = (ids: string[]) => ids.filter((id) => id === teacher.id || (isHelperJoinClass && id === helper.id));
      remoteVideoUserIds = filterIds(remoteVideoUserIds);
      remoteAudioUserIds = filterIds(remoteAudioUserIds);
    }

    // If the teacher doesn't allow students to see the helper's video, remove the helper's video from the list
    if (isHelperJoinClass && !helper.isVideoShownByTeacher && !isMeOneToOneWithHelper) {
      remoteVideoUserIds = remoteVideoUserIds.filter((id) => id !== helper.id);
    }

    // If the teacher and a student are in a one-to-one session
    if (oneToOneStudentId) {
      // If I am in one-to-one with the teacher
      if (isMeOneToOneWithTeacher) {
        remoteVideoUserIds = remoteVideoUserIds.filter((id) => id === teacher.id);
        remoteAudioUserIds = remoteAudioUserIds.filter((id) => id === teacher.id);
      } else {
        removeUserFromLists(oneToOneStudentId, remoteVideoUserIds, remoteAudioUserIds);
        removeUserFromLists(teacher.id, remoteVideoUserIds, remoteAudioUserIds);
      }
    }

    // If the helper and a student are in a one-to-one session
    if (isHelperJoinClass && helperOneToOneStudentId) {
      // If I am in one-to-one with the helper
      if (isMeOneToOneWithHelper) {
        const isMuteTeacherAudioWhenOneToOneWithHelper =
          rootGetters[vuexName(MODULES.CLASS_TEACHING.GETTERS.GET_IS_MUTE_TEACHER_AUDIO_WHEN_ONE_TO_ONE_WITH_HELPER)];

        remoteVideoUserIds = remoteVideoUserIds.filter((id) => id === helper.id || id === teacher.id);
        remoteAudioUserIds = remoteAudioUserIds.filter(
          (id) => (id === helper.id || id === teacher.id) && !(id === teacher.id && isMuteTeacherAudioWhenOneToOneWithHelper),
        );
      } else {
        removeUserFromLists(helperOneToOneStudentId, remoteVideoUserIds, remoteAudioUserIds);
        removeUserFromLists(helper?.id, remoteVideoUserIds, remoteAudioUserIds);
      }
    }
    return manager?.updateAudioAndVideoFeed([...new Set(remoteVideoUserIds)], [...new Set(remoteAudioUserIds)]);
  },
  async [defineVuexName(MODULES.STUDENT_ROOM.DISPATCHES.ENTER_CLASS)](store, _rejoin = false) {
    try {
      const { info, manager, user } = store.state;
      if (!info || !manager || !user) return;
      await RemoteTeachingService.studentEnterClass();
      if (!(manager as any).__studentRoomHandlersRegistered) {
        // IMPORTANT: Register realtime handlers and ensure they're ready before any switchChannels() calls
        // This prevents the race condition where channels are created before handlers are subscribed
        await manager.registerRealtimeEventHandler(useStudentRealtimeHandler(store));

        (manager as any).__studentRoomHandlersRegistered = true;
      }
    } catch (error) {
      Logger.error("ENTER_CLASS", error);
    }
  },
  async [defineVuexName(MODULES.STUDENT_ROOM.DISPATCHES.JOIN_ROOM)](store, _payload: any) {
    const { state, dispatch } = store;
    if (!state.info || !state.user) return;
    const {
      bandwidthUpdateBasedOnAgora,
      remoteUserJoined,
      remoteUserLeft,
      localUserConnectionChanged,
      handleUserPublishedAudio,
      handleUserPublishedVideo,
    } = callingUtils(store);
    const callingEventHandlers: AgoraEventHandler = {
      onLocalNetworkUpdate: (payload: NetworkQualityPayload) => {
        bandwidthUpdateBasedOnAgora(payload);
      },
      onUserJoined: ({ uid }: IAgoraRTCRemoteUser) => {
        const id = typeof uid === "string" ? uid : uid.toString();
        remoteUserJoined(id);
      },
      onUserLeft: async ({ uid }: IAgoraRTCRemoteUser, reason: string) => {
        const id = typeof uid === "string" ? uid : uid.toString();
        await remoteUserLeft(id, reason);
      },
      onConnectionStateChange: (curState: ConnectionState, prevState: ConnectionState, reason: ConnectionDisconnectedReason | undefined) => {
        localUserConnectionChanged(curState, prevState, reason);
      },
      onException: () => {
        //
      },
      onUserPublished: (userId: string, mediaType: "audio" | "video") => {
        if (mediaType === "audio") {
          handleUserPublishedAudio({ id: userId, status: MediaUserPublishStatus.PUBLISHED });
        } else if (mediaType === "video") {
          handleUserPublishedVideo({ id: userId, status: MediaUserPublishStatus.PUBLISHED });
        }
      },
      onUserUnPublished: (userId: string, mediaType: "audio" | "video") => {
        if (mediaType === "audio") {
          handleUserPublishedAudio({ id: userId, status: MediaUserPublishStatus.UNPUBLISHED });
        } else if (mediaType === "video") {
          handleUserPublishedVideo({ id: userId, status: MediaUserPublishStatus.UNPUBLISHED });
        }
      },
    };

    await state.manager?.join({
      classId: state.info?.id,
      studentId: state.user?.id,
      idOne: state.idOne,
      callingEventHandlers,
    });

    await dispatch(vuexName(MODULES.STUDENT_ROOM.DISPATCHES.UP_TO_DATE_SESSION_AFTER_RECONNECTED, false));
  },
  [defineVuexName(MODULES.STUDENT_ROOM.DISPATCHES.SET_SPEAKING_USERS)]({ commit }, payload: { level: number; uid: UID }[]) {
    const validSpeakings: Array<string> = [];
    if (payload) {
      payload.map((item) => {
        if (item.level >= AGORA_MINIMUM_USER_VOLUME) {
          // should check by a level
          validSpeakings.push(item.uid.toString());
        }
      });
    }
    commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_SPEAKING_USERS, false), { userIds: validSpeakings });
  },
  async [defineVuexName(MODULES.STUDENT_ROOM.DISPATCHES.LEAVE_ROOM)]({ state, commit }, payload: any) {
    await state.manager?.close(payload?.leave);
    if (!payload?.leave) {
      return;
    }
    commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.LEAVE_ROOM, false), payload);
    commit(vuexName(MODULES.CALLING.COMMITS.RESET_STATE), null, { root: true });
    commit(vuexName(MODULES.REALTIME_SETTINGS.COMMITS.RESET_STATE), null, { root: true });
    commit(vuexName(MODULES.LESSON.COMMITS.CLEAR_LESSON_DATA), null, { root: true });
    commit(vuexName(MODULES.LESSON.COMMITS.CLEAR_CACHE_IMAGE), null, { root: true });
    commit(vuexName(MODULES.LESSON.COMMITS.RESET_STATE), null, { root: true });
    commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_MODE), StudyMode.Normal, { root: true });
    commit(vuexName(MODULES.STUDENT_TEACHING.COMMITS.SET_CURRENT_INDEPENDENT_ITEM_ID), null, { root: true });
  },
  async [defineVuexName(MODULES.STUDENT_ROOM.DISPATCHES.SET_STUDENT_AUDIO)]({ dispatch }, payload: SetStudentMediaDeviceAction) {
    await dispatch(vuexName(MODULES.STUDENT_ROOM.DISPATCHES.TOGGLE_CURRENT_STUDENT_MICROPHONE, false), payload);
  },
  async [defineVuexName(MODULES.STUDENT_ROOM.DISPATCHES.TOGGLE_CURRENT_STUDENT_MICROPHONE)]({ state }, payload: SetStudentMediaDeviceAction) {
    try {
      await state.manager?.setMicrophone({ enable: payload.enable });
    } catch (err) {
      Logger.error(err);
      notification.error({
        message: fmtMsg(ErrorLocale.ToggleMicroError),
      });
    }
  },
  async [defineVuexName(MODULES.STUDENT_ROOM.DISPATCHES.SET_STUDENT_VIDEO)]({ dispatch }, payload: SetStudentMediaDeviceAction) {
    await dispatch(vuexName(MODULES.STUDENT_ROOM.DISPATCHES.TOGGLE_CURRENT_STUDENT_CAMERA, false), payload);
  },
  async [defineVuexName(MODULES.STUDENT_ROOM.DISPATCHES.TOGGLE_CURRENT_STUDENT_CAMERA)]({ state }, payload: SetStudentMediaDeviceAction) {
    try {
      await state.manager?.setCamera({ enable: payload.enable });
    } catch (err) {
      Logger.error(err);
      notification.error({
        message: fmtMsg(ErrorLocale.ToggleCameraError),
      });
    }
  },
  async [defineVuexName(MODULES.STUDENT_ROOM.DISPATCHES.STUDENT_RAISING_HAND)]({ commit, state }, payload: boolean) {
    commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_STUDENT_RAISING_HAND, false), { raisingHand: payload });
    const studentId = state.student?.id;
    const sessionId = state.info?.id;
    if (!studentId || !sessionId) return;
    await RemoteTeachingService.raisingHand(payload);
  },
  [defineVuexName(MODULES.STUDENT_ROOM.DISPATCHES.SET_ONLINE)]({ commit }) {
    commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_ONLINE, false));
  },
  [defineVuexName(MODULES.STUDENT_ROOM.DISPATCHES.SET_OFFLINE)]({ commit, rootState }) {
    if (rootState.app.userRole === UserRole.Student) {
      commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_OFFLINE, false));
    }
  },
  async [defineVuexName(MODULES.STUDENT_ROOM.DISPATCHES.STUDENT_LEAVE_CLASS)]({ state }) {
    const sessionId = state.info?.id;
    const studentId = state.user?.id;
    if (!sessionId || !studentId) return;
    await RemoteTeachingService.studentLeaveSession(studentId, sessionId);
  },
  async [defineVuexName(MODULES.STUDENT_ROOM.DISPATCHES.GET_AVATAR_TEACHER)]({ commit }, payload: { teacherId: string }) {
    const response = await InfoService.fetchTeacherAvatarUrl(payload.teacherId);
    if (response) commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_AVATAR_TEACHER, false), response);
  },
  async [defineVuexName(MODULES.STUDENT_ROOM.DISPATCHES.GET_AVATAR_HELPER)]({ commit }, payload: { helperId: string }) {
    const response = await InfoService.fetchTeacherAvatarUrl(payload.helperId);
    if (response) commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_AVATAR_HELPER, false), response);
  },
  async [defineVuexName(MODULES.STUDENT_ROOM.DISPATCHES.SET_CLASS_DATA)]({ commit, dispatch, state, rootGetters }, roomInfo: RoomSessionModel) {
    // set members data: teacher, helper, students ...
    applySettings(commit, roomInfo.settings);
    commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_ROOM_INFO, false), roomInfo);
    // setup lesson plan sidebar data; if isSetCurrentExposure === true, set default Exposure and Slide is first item
    if (!roomInfo.options?.isActionUpToDate) {
      await dispatch(
        vuexName(MODULES.LESSON.DISPATCHES.SET_INFO),
        {
          lessonPlan: roomInfo?.lessonPlan,
          isSetCurrentExposure: roomInfo?.lessonPlan.selectedExposureId && !(state.student?.id === roomInfo.oneAndOneDto?.studentId),
        },
        { root: true },
      );
    }
    // set users in one to one mode with teacher and helper
    if (roomInfo.oneAndOneDto?.studentId) {
      commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_STUDENT_ONE_ID), { id: roomInfo.oneAndOneDto.studentId, withTeacher: true }, { root: true });
    } else {
      commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_STUDENT_ONE_ID), { id: "", withTeacher: true }, { root: true });
    }
    if (roomInfo.helperOneAndOneDto?.studentId) {
      commit(
        vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_STUDENT_ONE_ID),
        { id: roomInfo.helperOneAndOneDto.studentId, withTeacher: false },
        { root: true },
      );
    } else {
      commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_STUDENT_ONE_ID), { id: "", withTeacher: false }, { root: true });
    }

    const isUserOneOneTeacher = state.student?.id === roomInfo.oneAndOneDto?.studentId;
    const isUserOneOneHelper = state.student?.id === roomInfo.helperOneAndOneDto?.studentId;
    if (
      !isUserOneOneTeacher &&
      !isUserOneOneHelper &&
      !isTeacherUseOnlySlideVisible({
        isTeacherUseOnly: !!rootGetters[vuexName(MODULES.LESSON.GETTERS.GET_CURRENT_EXPOSURE_ITEM_MEDIA)]?.teacherUseOnly,
        isWhiteboardVisible: roomInfo.state.isShowWhiteBoard,
      })
    ) {
      // full sync objects
      commit(
        vuexName(MODULES.ANNOTATION_V2.COMMITS.SET_OBJECTS),
        {
          objects: mapObjectsFromBE(roomInfo.annotation?.drawing?.objects),
        },
        { root: true },
      );
    }

    let studentOneToOne: string | undefined;
    let oneAndOneDto: OneToOneDto | null | undefined;
    if (isUserOneOneTeacher) {
      commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_INITIAL_ONE_TO_ONE_DATA), roomInfo.oneAndOneDto, { root: true });
      studentOneToOne = roomInfo.oneAndOneDto?.studentId;
      oneAndOneDto = roomInfo.oneAndOneDto;
    }
    if (isUserOneOneHelper) {
      commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_INITIAL_ONE_TO_ONE_DATA), roomInfo.helperOneAndOneDto, { root: true });
      studentOneToOne = roomInfo.helperOneAndOneDto?.studentId;
      oneAndOneDto = roomInfo.helperOneAndOneDto;
    }

    // data related to Target feature
    const fillTargets = async (targets: TeachingActivityTarget[] | undefined) => {
      await dispatch(vuexName(MODULES.LESSON.DISPATCHES.PROCESS_VISIBLE_TARGETS_DATA_FROM_API), targets, {
        root: true,
      });
    };
    // does whiteboard open or not.
    const fillWhiteboardStatus = (opened: boolean) => {
      commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_WHITEBOARD, false), opened);
    };
    // canvas (board) zoom ratio
    const fillBoardZoomRatio = (ratio: number) => {
      commit(vuexName(MODULES.LESSON.COMMITS.SET_ZOOM_RATIO), ratio, { root: true });
    };

    const fillBoardZoomPosition = (coords: { x: number; y: number } | null) => {
      commit(vuexName(MODULES.LESSON.COMMITS.SET_IMG_COORDS), coords ?? undefined, { root: true });
    };

    const fillBoardPdfScrolledRatio = (ratio: number) => {
      commit(vuexName(MODULES.LESSON.COMMITS.SET_PDF_SCROLL_PROGRESS), ratio, { root: true });
    };

    //** Current student is in 1:1 mode with teacher/helper */
    if (studentOneToOne) {
      if (!oneAndOneDto) throw new Error("Something went wrong!");
      const {
        isEnablePalette = false,
        isShowWhiteBoard,
        ratio,
        position,
        ratioScrollPdf,
        studentId,
        selectedExposureId,
        selectedExposureItemId,
      } = oneAndOneDto;
      commit(
        vuexName(MODULES.LESSON.COMMITS.SET_CURRENT_EXPOSURE),
        { id: selectedExposureId, preventSelectFirstSlideAutomatically: true },
        { root: true },
      );
      commit(vuexName(MODULES.LESSON.COMMITS.SET_CURRENT_EXPOSURE_ITEM_MEDIA), { id: selectedExposureItemId }, { root: true });
      commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_STUDENT_PALETTE, false), {
        studentId: studentId,
        isPaletteEnabled: isEnablePalette,
      });
      fillWhiteboardStatus(isShowWhiteBoard);
      commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_CLASS_ACTION, false), {
        action: ClassActionFromValue(oneAndOneDto.lessonAction),
      });
      if (roomInfo.annotation) {
        const isTeacherOnlyForOneToOne = !!rootGetters[vuexName(MODULES.LESSON.GETTERS.GET_CURRENT_EXPOSURE_ITEM_MEDIA)]?.teacherUseOnly;
        const isWbVisibleForOneToOne = isShowWhiteBoard;
        const isNeedToSetAnnotationInsideOneToOne = !(isTeacherOnlyForOneToOne && !isWbVisibleForOneToOne);
        if (isNeedToSetAnnotationInsideOneToOne) {
          // annotation data inside 1:1: targets
          await fillTargets(roomInfo.annotation.oneOneDrawing.targets);
          // full sync objects
          commit(
            vuexName(MODULES.ANNOTATION_V2.COMMITS.SET_OBJECTS),
            {
              objects: mapObjectsFromBE(roomInfo.annotation?.oneOneDrawing?.objects),
            },
            { root: true },
          );
        }
      }
      fillBoardZoomRatio(ratio);
      fillBoardPdfScrolledRatio(ratioScrollPdf);
      fillBoardZoomPosition(position ? { x: position.x, y: position.y } : null);
    } else {
      if (roomInfo.options?.isActionUpToDate)
        await dispatch(vuexName(MODULES.LESSON.DISPATCHES.SET_BOTH_EXPOSURE_AND_SLIDE), roomInfo.lessonPlan, { root: true });
      fillWhiteboardStatus(roomInfo.state.isShowWhiteBoard);
      if (
        roomInfo.annotation &&
        !isTeacherUseOnlySlideVisible({
          isTeacherUseOnly: !!rootGetters[vuexName(MODULES.LESSON.GETTERS.GET_CURRENT_EXPOSURE_ITEM_MEDIA)]?.teacherUseOnly,
          isWhiteboardVisible: roomInfo.state.isShowWhiteBoard,
        })
      ) {
        // annotation data outside 1:1: targets
        await fillTargets(roomInfo.annotation.drawing.targets);
      }
      fillBoardZoomRatio(roomInfo.lessonPlan.ratio);
      fillBoardPdfScrolledRatio(roomInfo.state.ratioScrollPdf);
      fillBoardZoomPosition(roomInfo.lessonPlan.position ? { x: roomInfo.lessonPlan.position.x, y: roomInfo.lessonPlan.position.y } : null);
    }
  },
  async [defineVuexName(MODULES.STUDENT_ROOM.DISPATCHES.SET_API_ERROR)]({ commit }, payload: any) {
    // TODO: Define code on server side and use it here rather than using message
    if (payload?.message === "Session is not found") {
      commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_API_STATUS, false), {
        code: GLErrorCode.CLASS_IS_NOT_ACTIVE,
        message: fmtMsg(ErrorLocale.ClassNotStarted),
      });
    } else if (payload.code === ErrorCode.ConcurrentUserException) {
      showUnableJoinClassErrorNotification(11);
      await router.push(Paths.Home);
    } else if (payload.code === ErrorCode.StudentNotInClass) {
      commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_API_STATUS, false), {
        code: GLErrorCode.PARENT_NOT_HAVE_THIS_STUDENT,
        message: fmtMsg(ErrorLocale.ParentAccountNotHaveThisStudent),
      });
    }
  },
  async [defineVuexName(MODULES.STUDENT_ROOM.DISPATCHES.CHANGE_INDEPENDENT_ITEM)]({ state }, payload: { currentItemId: string; progress: number }) {
    const studentId = state.student?.id;
    const currentUnit = state.info?.classInfo.unit;
    const currentLesson = state.info?.classInfo.lesson;
    const sessionId = state.info?.id;
    if (!studentId || !currentUnit || !currentLesson || !sessionId) return;
    const { currentItemId } = payload;

    const messageData: StudentUpdateIndependentExposureEventData = {
      studentId: studentId,
      currentItemId: currentItemId,
    };

    const realtimeTransport = state.manager?.WSClient.getRealtimeTransport();
    if (realtimeTransport) {
      await realtimeTransport.sendToAll({
        event: REALTIME_EVENT.STUDENT,
        action: StudentAction.UPDATE_INDEPENDENT_EXPOSURE_ITEM,
        data: messageData,
      });
    }

    // Call the debounced function for updating cache
    const request: UpdateIndependentExposureItemModel = {
      sessionId: sessionId,
      studentId: studentId,
      currentItemId: currentItemId,
    };

    debouncedUpdateIndependentItem(request);
  },
  async updateCameraDevice({ state }, { blockReopen }: { blockReopen?: boolean }) {
    await state.manager?.updateCameraDevice();
    if (blockReopen) return;
    await state.manager?.setCamera({
      enable: false,
      videoEncoderConfigurationPreset: "480p",
    });
    await state.manager?.setCamera({
      enable: true,
      videoEncoderConfigurationPreset: "480p",
    });
  },
  async updateMicrophoneDevice({ state }) {
    await state.manager?.updateMicrophoneDevice();
  },
  async updateSpeakerDevice({ state }) {
    await state.manager?.updateSpeakerDevice();
  },
  async [defineVuexName(MODULES.STUDENT_ROOM.DISPATCHES.UP_TO_DATE_SESSION_AFTER_RECONNECTED)]({ state, rootState, commit, dispatch }) {
    const sessionId = rootState.classTeaching.sessionId;
    const studentId = state.student?.id;
    if (!sessionId || !studentId) return;
    const toTeamMode = (enabled: boolean) => {
      commit(vuexName(MODULES.TEAMS.COMMITS.SET_IS_TEAM_MODE), enabled, { root: true });
    };
    const toIndependentMode = (enabled: boolean) => {
      commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_MODE), enabled ? StudyMode.Independent : StudyMode.Normal, { root: true });
    };
    try {
      const response = await RemoteTeachingService.fetchSessionDtoById(sessionId);
      const roomInfo = response.data;
      toTeamMode(roomInfo.state.isTeamMode);
      toIndependentMode(roomInfo.state.independentMode);
      await dispatch(vuexName(MODULES.STUDENT_ROOM.DISPATCHES.SET_CLASS_DATA, false), {
        ...response.data,
        options: {
          isActionUpToDate: true,
        },
      });
    } catch (e) {
      Logger.error(e);
    }
  },
  async [defineVuexName(MODULES.STUDENT_ROOM.DISPATCHES.HANDLE_TEACHER_TOGGLE_STUDENT_MEDIA_DEVICES)](
    { dispatch, state },
    payload: ToggleStudentMediaDevicesAsync,
  ) {
    const { mediaDeviceType, isMute, studentIds } = payload;
    const studentId = state.student?.id;
    if (studentId && studentIds.includes(studentId)) {
      const toggleAction =
        mediaDeviceType === MediaDeviceTypes.Microphone
          ? vuexName(MODULES.STUDENT_ROOM.DISPATCHES.TOGGLE_CURRENT_STUDENT_MICROPHONE, false)
          : mediaDeviceType === MediaDeviceTypes.Camera
          ? vuexName(MODULES.STUDENT_ROOM.DISPATCHES.TOGGLE_CURRENT_STUDENT_CAMERA, false)
          : null;

      if (toggleAction) {
        await dispatch(toggleAction, { enable: !isMute });
      }
    }
  },
  async [defineVuexName(MODULES.STUDENT_ROOM.DISPATCHES.HANDLE_REMOTE_USER_JOINED)]({ commit, state }, id: string) {
    const { teacher } = state;
    commit(vuexName(MODULES.CALLING.COMMITS.SET_CALLING_USER_IDS), { id, status: CallingUserStatus.JOINED }, { root: true });
    teacher?.id === id && commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_TEACHER_DISCONNECTED, false), false);
  },
};

export default actions;
